import { useCallback, useEffect, useState } from 'react';
import {
  ARTIFACTS,
  ARTIFACT_KEYS,
  BACKPACKS,
  GEAR,
  HELMETS,
  LANTERNS,
  MINE_ZONES,
  ORES,
  ORE_KEYS,
  PICKAXES,
  PICKAXE_KEYS,
  zoneFor,
  zoneHardness,
  WORN_SHARE,
  type GearSlot,
  type MineStatusDto,
  type OreKey,
  type PickaxeKey,
} from '@bb/shared';
import { ApiError, mine as mineApi, type MineEnded, type MineEventDto, type MineResponse, type ProgressNews } from '../net/api';
import { play } from '../audio/sound';
import type { AvatarLook } from '../art/avatar';
import { Sheet } from './Sheet';
import { MineScene } from './MineScene';

type Tab = 'camp' | 'forge' | 'assay' | 'treasures';
type Cost = { coins: number; materials: Partial<Record<OreKey, number>> };

const nameOf = (k: string) => (k in ORES ? ORES[k as OreKey].name : k in ARTIFACTS ? ARTIFACTS[k as keyof typeof ARTIFACTS].name : k);

/** The Deep Mine under the Pebble Beach cliffs: camp, forge, assay office and the dives themselves. */
export function Mine({ look, onClose, onChanged }: { look: AvatarLook; onClose: () => void; onChanged: (news?: ProgressNews) => void }) {
  const [s, setS] = useState<MineStatusDto>();
  const [tab, setTab] = useState<Tab>('camp');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string }>();
  const [log, setLog] = useState<MineEventDto[]>([]);
  const [ended, setEnded] = useState<MineEnded>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    mineApi.get().then(setS, (e: Error) => setMsg({ kind: 'err', text: e.message }));
  }, []);

  /** Runs one mine action; resolves true when the server accepted it. */
  const run = useCallback(
    async (fn: () => Promise<MineResponse>): Promise<boolean> => {
      if (busy) return false;
      setBusy(true);
      setMsg(undefined);
      try {
        const r = await fn();
        setS(r.status);
        if (r.events.length) {
          setLog((l) => [...r.events, ...l].slice(0, 6));
          const kinds = r.events.map((e) => e.kind);
          if (kinds.includes('fainted') || kinds.includes('gas') || kinds.includes('rockfall')) play('error');
          else if (kinds.includes('level') || kinds.includes('badge')) play('levelUp');
          else if (kinds.includes('chest') || kinds.includes('artifact') || kinds.includes('key')) play('chime');
          else if (kinds.includes('ore')) play('coin');
        }
        if (r.ended) {
          setEnded(r.ended);
          setLog([]);
        }
        if (r.ended || r.progress) onChanged(r.progress);
        return true;
      } catch (e) {
        // A dig let go a moment too early (network delay) just doesn't count; no need to buzz.
        if (!(e instanceof ApiError && e.code === 'dig_too_quick')) play('error');
        setMsg({ kind: 'err', text: e instanceof ApiError || e instanceof Error ? e.message : 'Something went wrong.' });
        return false;
      } finally {
        setBusy(false);
      }
    },
    [busy, onChanged],
  );

  const dive = s?.dive;
  // Inside the mine the whole screen is the mine.
  if (s && dive) {
    return (
      <MineScene
        status={s}
        dive={dive}
        look={look}
        log={log}
        busy={busy}
        error={msg?.kind === 'err' ? msg.text : undefined}
        onAct={(i) => run(() => mineApi.dig(i))}
        onDescend={() => void run(mineApi.descend)}
        onClimb={() => void run(mineApi.climbOut)}
      />
    );
  }
  return (
    <Sheet title="Deep Mine" subtitle={s ? `Mining level ${s.level} · deepest floor ${s.deepest || '—'}` : undefined} onClose={onClose} wide>
      {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
      {!s ? (
        <p className="muted">Lighting the lanterns…</p>
      ) : (
        <>
          {ended && <Summary ended={ended} onDone={() => setEnded(undefined)} />}
          <div className="tabs" role="tablist">
            {(['camp', 'forge', 'assay', 'treasures'] as Tab[]).map((t) => (
              <button key={t} role="tab" aria-selected={tab === t} className={tab === t ? 'tab on' : 'tab'} onClick={() => setTab(t)}>
                {{ camp: 'Camp', forge: 'Forge', assay: 'Assay Office', treasures: 'Treasures' }[t]}
              </button>
            ))}
          </div>
          {tab === 'camp' && <Camp s={s} busy={busy} onDive={(camp) => { setEnded(undefined); void run(() => mineApi.dive(camp)); }} onForge={() => setTab('forge')} />}
          {tab === 'forge' && <Forge s={s} onMade={(st, text, news) => { setS(st); setMsg({ kind: 'ok', text }); play('levelUp'); onChanged(news); }} onError={(text) => setMsg({ kind: 'err', text })} />}
          {tab === 'assay' && <Assay s={s} onSold={(st, text, news) => { setS(st); setMsg({ kind: 'ok', text }); play('coin'); onChanged(news); }} onError={(text) => setMsg({ kind: 'err', text })} />}
          {tab === 'treasures' && <Treasures s={s} />}
        </>
      )}
    </Sheet>
  );
}

function XpBar({ s }: { s: MineStatusDto }) {
  const pct = s.nextLevelXp === null ? 100 : Math.round(((s.xp - s.levelXp) / (s.nextLevelXp - s.levelXp)) * 100);
  return (
    <div className="mine-xp" aria-label={`Mining level ${s.level}`}>
      <div className="mine-xp-top"><b>Mining level {s.level}</b><span className="muted">{s.nextLevelXp === null ? 'Max level!' : `${s.xp.toLocaleString()} / ${s.nextLevelXp.toLocaleString()} XP`}</span></div>
      <div className="meter"><i style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function Camp({ s, busy, onDive, onForge }: { s: MineStatusDto; busy: boolean; onDive: (camp: number) => void; onForge: () => void }) {
  const pick = PICKAXES[s.gear.pickaxe];
  const broken = s.durability <= 0;
  return (
    <div className="stack">
      <XpBar s={s} />
      <div className="mine-gear">
        <div><span className="muted small-print">Pickaxe</span><b>{pick.name}</b><span className="small-print">Breaks rock up to hardness {pick.power}</span><Durability s={s} /></div>
        <div><span className="muted small-print">Lantern</span><b>{s.energy} energy</b><span className="small-print">Light reaches {LANTERNS[s.gear.lantern]!.light} square{LANTERNS[s.gear.lantern]!.light > 1 ? 's' : ''}</span></div>
        <div><span className="muted small-print">Backpack</span><b>{BACKPACKS[s.gear.backpack]!.slots} slots</b><span className="small-print">Ores and artifacts</span></div>
        <div><span className="muted small-print">Helmet</span><b>{HELMETS[s.gear.helmet]!.protection ? `${Math.round(HELMETS[s.gear.helmet]!.protection * 100)}% safer` : 'None yet'}</b><span className="small-print">Against gas and falling rocks</span></div>
      </div>
      <h3 className="mine-h">Go down the mine</h3>
      {broken && (
        <p className="form-error small-print" role="status">
          Your {pick.name} is broken. <button className="link-btn" onClick={onForge}>Repair or replace it at the Forge</button> before going down.
        </p>
      )}
      <p className="muted small-print">Walk the tunnels and hold toward a rock to dig it: harder rock takes longer, and so do rocks with something inside. Every dig uses lantern energy and wears your pickaxe. Climb out any time to keep everything you're carrying. If your lantern runs out, Pip the canary leads you back up, but you drop half your ores.</p>
      <div className="camp-list">
        {s.camps.map((c) => {
          const zone = zoneFor(c);
          const tooHard = pick.power < zoneHardness(zone);
          return (
            <button key={c} className="camp" disabled={busy || tooHard || broken} onClick={() => onDive(c)} style={{ borderColor: zone.color }}>
              <b>Floor {c}</b>
              <span>{zone.name}</span>
              {tooHard && <span className="small-print form-error">Needs a stronger pickaxe</span>}
            </button>
          );
        })}
      </div>
      <details>
        <summary>Zones of the mine</summary>
        <table className="mine-table">
          <thead><tr><th>Floors</th><th>Zone</th><th>Pickaxe power</th><th>Dangers</th></tr></thead>
          <tbody>
            {MINE_ZONES.map((z) => (
              <tr key={z.key}>
                <td>{z.from}–{z.to}</td>
                <td>{z.name}</td>
                <td>{zoneHardness(z)}</td>
                <td>{[z.gas ? 'gas' : '', z.rockfall ? 'falling rocks' : '', z.lava ? 'lava' : ''].filter(Boolean).join(', ') || 'none'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

function Durability({ s }: { s: MineStatusDto }) {
  const pct = Math.round((s.durability / Math.max(1, s.maxDurability)) * 100);
  return (
    <span className="durability">
      <span className={pct <= Math.round(WORN_SHARE * 100) ? 'meter low' : 'meter pick'} aria-label={`Durability ${s.durability} of ${s.maxDurability}`}><i style={{ width: `${pct}%` }} /></span>
      <span className="small-print">{s.durability <= 0 ? 'Broken' : `${s.durability} / ${s.maxDurability}`}</span>
    </span>
  );
}

function CostLine({ cost, s }: { cost: Cost; s: MineStatusDto }) {
  const parts = Object.entries(cost.materials).map(([o, n]) => {
    const have = s.ores[o as OreKey] ?? 0;
    return <span key={o} className={have >= (n ?? 0) ? 'have' : 'need'}>{n} {ORES[o as OreKey].name.toLowerCase()} ({have})</span>;
  });
  return <span className="cost">{cost.coins > 0 && <span>{cost.coins.toLocaleString()} coins</span>}{parts}</span>;
}

function Forge({ s, onMade, onError }: { s: MineStatusDto; onMade: (s: MineStatusDto, text: string, news?: ProgressNews) => void; onError: (t: string) => void }) {
  const make = async (body: { kind: 'pickaxe'; pickaxe: PickaxeKey } | { kind: GearSlot }) => {
    try {
      const r = await mineApi.craft(body);
      onMade(r.status, `You made the ${r.made}!`, r.progress);
    } catch (e) {
      onError((e as Error).message);
    }
  };
  const current = PICKAXE_KEYS.indexOf(s.gear.pickaxe);
  const repair = async () => {
    try {
      const r = await mineApi.repair();
      onMade(r.status, `Your ${r.repaired} is fixed!`);
    } catch (e) {
      onError((e as Error).message);
    }
  };
  const mine = PICKAXES[s.gear.pickaxe];
  const worn = s.durability < s.maxDurability;
  const lost = mine.durability - s.maxDurability;
  return (
    <div className="stack">
      <h3 className="mine-h">Your pickaxe</h3>
      <div className="forge-item">
        <span className="pick-swatch" style={{ background: mine.color }} aria-hidden="true">⛏</span>
        <div className="forge-text">
          <b>{mine.name}</b>
          <Durability s={s} />
          <span className="small-print">
            {s.durability <= 0 ? 'Broken: it can’t dig until it’s fixed.' : worn ? 'Repairing tops it back up.' : 'In great shape!'}
            {lost > 0 && ` Repairs have worn it down by ${lost} (of ${mine.durability}); a new one is as good as new.`}
          </span>
          {worn && <CostLine cost={s.repair} s={s} />}
        </div>
        <div className="row-wrap">
          {worn && <button className="btn small" onClick={() => void repair()}>{s.repair.coins ? 'Repair' : 'Repair (free)'}</button>}
          {(worn || lost > 0) && s.gear.pickaxe !== 'wooden' && (
            <button className="btn small ghost" onClick={() => void make({ kind: 'pickaxe', pickaxe: s.gear.pickaxe })}>Replace</button>
          )}
        </div>
      </div>
      {(worn || lost > 0) && s.gear.pickaxe !== 'wooden' && (
        <p className="muted small-print">Replace makes a brand-new {mine.name} for its full price: <CostLine cost={mine.cost} s={s} /></p>
      )}
      <h3 className="mine-h">Pickaxes</h3>
      <div className="forge-list">
        {PICKAXE_KEYS.map((k, i) => {
          const p = PICKAXES[k];
          const owned = i <= current;
          return (
            <div key={k} className={owned ? 'forge-item owned' : 'forge-item'}>
              <span className="pick-swatch" style={{ background: p.color }} aria-hidden="true">⛏</span>
              <div className="forge-text">
                <b>{p.name}{k === s.gear.pickaxe ? ' (yours)' : ''}</b>
                <span className="small-print">Hardness {p.power} · {p.efficiency < 1 ? `digs faster and cost ${Math.round((1 - p.efficiency) * 100)}% less energy` : 'standard digging'} · lasts {p.durability} · Mining level {p.level}</span>
                {!owned && <CostLine cost={p.cost} s={s} />}
              </div>
              {!owned && <button className="btn small" disabled={s.level < p.level} onClick={() => void make({ kind: 'pickaxe', pickaxe: k })}>{s.level < p.level ? `Level ${p.level}` : 'Make'}</button>}
            </div>
          );
        })}
      </div>
      <h3 className="mine-h">Gear</h3>
      <div className="forge-list">
        {(Object.keys(GEAR) as GearSlot[]).map((slot) => {
          const levels = GEAR[slot].levels;
          const at = s.gear[slot];
          const next = levels[at + 1];
          const describe = (lvl: number) =>
            slot === 'lantern' ? `${LANTERNS[lvl]!.energy} energy, light ${LANTERNS[lvl]!.light}` : slot === 'backpack' ? `${BACKPACKS[lvl]!.slots} slots` : `${Math.round(HELMETS[lvl]!.protection * 100)}% protection`;
          return (
            <div key={slot} className="forge-item">
              <div className="forge-text">
                <b>{GEAR[slot].name} · level {at + 1} of {levels.length}</b>
                <span className="small-print">Now: {describe(at)}{next ? ` → next: ${describe(at + 1)} (Mining level ${next.level})` : ' · the best there is'}</span>
                {next && <CostLine cost={next.cost} s={s} />}
              </div>
              {next && <button className="btn small" disabled={s.level < next.level} onClick={() => void make({ kind: slot })}>{s.level < next.level ? `Level ${next.level}` : 'Upgrade'}</button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Assay({ s, onSold, onError }: { s: MineStatusDto; onSold: (s: MineStatusDto, text: string, news?: ProgressNews) => void; onError: (t: string) => void }) {
  const sell = async (ore?: OreKey) => {
    try {
      const r = await mineApi.sell(ore);
      onSold(r.status, `Sold for ${r.coins} coins.`, r.progress);
    } catch (e) {
      onError((e as Error).message);
    }
  };
  const owned = ORE_KEYS.filter((o) => (s.ores[o] ?? 0) > 0).sort((a, b) => ORES[b].sell - ORES[a].sell);
  const left = Math.max(0, s.assay.dailyLimit - s.assay.soldToday);
  return (
    <div className="stack">
      <p className="muted small-print">The Assay Office buys up to {s.assay.dailyLimit.toLocaleString()} coins of ore a day (more as your Mining level grows). {left.toLocaleString()} left today. Keep the ores you need for the Forge!</p>
      <div className="meter"><i style={{ width: `${Math.min(100, (s.assay.soldToday / s.assay.dailyLimit) * 100)}%` }} /></div>
      {!owned.length ? (
        <p className="muted">No ores yet. Go dig some up!</p>
      ) : (
        <>
          <table className="mine-table">
            <thead><tr><th>Ore</th><th className="num">You have</th><th className="num">Each</th><th /></tr></thead>
            <tbody>
              {owned.map((o) => (
                <tr key={o}>
                  <td><span className="ore-dot" style={{ background: ORES[o].color }} /> {ORES[o].name}</td>
                  <td className="num">{s.ores[o]}</td>
                  <td className="num">{ORES[o].sell}</td>
                  <td><button className="btn ghost small" disabled={!left} onClick={() => void sell(o)}>Sell</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="btn" disabled={!left} onClick={() => void sell()}>Sell everything (best first)</button>
        </>
      )}
    </div>
  );
}

function Treasures({ s }: { s: MineStatusDto }) {
  return (
    <div className="stack">
      <p className="muted small-print">{s.artifacts.length} of {ARTIFACT_KEYS.length} artifacts found. Find them all to finish the Deep Mine Treasures collection.</p>
      <div className="treasures">
        {ARTIFACT_KEYS.map((a) => {
          const found = s.artifacts.includes(a);
          return (
            <div key={a} className={found ? 'treasure found' : 'treasure'}>
              <span aria-hidden="true">{found ? '✨' : '❔'}</span>
              <b>{found ? ARTIFACTS[a].name : '???'}</b>
              <span className="small-print">{found ? ARTIFACTS[a].description : `Somewhere below floor ${ARTIFACTS[a].minFloor}`}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Summary({ ended, onDone }: { ended: MineEnded; onDone: () => void }) {
  const items = Object.entries(ended.kept).filter(([, n]) => (n ?? 0) > 0);
  return (
    <div className={ended.fainted ? 'mine-summary fainted' : 'mine-summary'} role="status">
      <b>{ended.fainted ? 'Your lantern went out on floor ' : 'You climbed out from floor '}{ended.deepestFloor}.</b>
      <span>{items.length ? `You kept ${items.map(([k, n]) => `${n} ${nameOf(k)}`).join(', ')}` : 'You came back empty-handed'}{ended.coins ? ` and ${ended.coins} coins from chests` : ''}.</span>
      {ended.fainted && <span className="small-print">Pip the canary led you out, but you dropped half your ores. Climb out before your lantern runs low!</span>}
      <button className="btn ghost small" onClick={onDone}>OK</button>
    </div>
  );
}
